import type { ReactNode } from 'react'

/**
 * One accordion card inside a {@link SearchSheet}. Collapsed, it shows a compact
 * "label → summary" row (e.g. "When → Add dates"); expanded, it shows a large
 * heading and its editor content. Only one section in a sheet is expanded at a
 * time — the parent owns which. Mirrors Airbnb's stacked Where / When / Who.
 */
export function SearchSection({
  label,
  heading,
  summary,
  expanded,
  onExpand,
  children,
}: {
  /** Short collapsed label, e.g. "When". */
  label: string
  /** Expanded heading; defaults to `${label}?`. */
  heading?: string
  /** Collapsed-state value, e.g. "Add dates" or the chosen dates. */
  summary: string
  expanded: boolean
  onExpand: () => void
  children: ReactNode
}) {
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onExpand}
        className="flex w-full items-center justify-between gap-4 rounded-2xl bg-white px-5 py-4 text-left shadow-sm transition hover:shadow-md"
      >
        <span className="text-sm font-medium text-stone-600">{label}</span>
        <span className="truncate text-sm font-semibold text-stone-900">{summary}</span>
      </button>
    )
  }

  return (
    <section className="rounded-2xl bg-white px-5 py-5 shadow-xl">
      <h2 className="mb-4 text-2xl font-bold tracking-tight text-stone-900">
        {heading ?? `${label}?`}
      </h2>
      {children}
    </section>
  )
}
